import type {ReactNode} from 'react';

import {useLayoutEffect, useMemo, useRef, useState} from 'react';

import {cn} from '../../helpers/cn';

export type TabsItem = {
  key: string;
  label: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
};

export type TabsProps = {
  items: TabsItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  className?: string;
  tabBarClassName?: string;
  fullWidth?: boolean;
};

type InkBarStyle = {
  left: number;
  width: number;
};

export function Tabs({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  className,
  tabBarClassName,
  fullWidth = false
}: TabsProps) {
  const isControlled = activeKey !== undefined;
  const firstKey = items[0]?.key ?? '';
  const [uncontrolledKey, setUncontrolledKey] = useState(defaultActiveKey ?? firstKey);
  const currentKey = isControlled ? activeKey : uncontrolledKey;
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [inkBar, setInkBar] = useState<InkBarStyle | null>(null);
  const [inkAnimate, setInkAnimate] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const isFirstMeasure = useRef(true);
  const isFirstScroll = useRef(true);

  const setActiveKey = (key: string) => {
    if (!isControlled) {
      setUncontrolledKey(key);
    }
    onChange?.(key);
  };

  const activeItem = useMemo(
    () => items.find((item) => item.key === currentKey) ?? items[0],
    [items, currentKey]
  );

  const checkScrollState = () => {
    if (!scrollRef.current) return;
    const {scrollLeft, scrollWidth, clientWidth} = scrollRef.current;
    if (scrollWidth <= clientWidth) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const threshold = 1.5;
    const absScroll = Math.abs(scrollLeft);
    setCanScrollRight(absScroll > threshold);
    setCanScrollLeft(absScroll + clientWidth < scrollWidth - threshold);
  };

  useLayoutEffect(() => {
    const tabList = tabListRef.current;
    const activeTab = tabRefs.current[currentKey];
    const scrollContainer = scrollRef.current;

    const measureInkBar = (): InkBarStyle => {
      if (!activeTab) return {left: 0, width: 0};
      return {
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth
      };
    };

    const updateLayout = () => {
      if (activeTab) {
        const next = measureInkBar();
        if (isFirstMeasure.current) {
          setInkBar(next);
          isFirstMeasure.current = false;
        } else {
          setInkAnimate(true);
          setInkBar(next);
        }
      }
      checkScrollState();
    };

    updateLayout();

    const observer = new ResizeObserver(updateLayout);
    if (tabList) observer.observe(tabList);
    if (activeTab) observer.observe(activeTab);
    if (scrollContainer) observer.observe(scrollContainer);

    return () => observer.disconnect();
  }, [currentKey, items]);

  useLayoutEffect(() => {
    const activeTab = tabRefs.current[currentKey];
    if (!activeTab) return;

    activeTab.scrollIntoView({
      behavior: isFirstScroll.current ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
    isFirstScroll.current = false;
  }, [currentKey, items]);

  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          'relative px-2',
          'before:pointer-events-none before:absolute before:inset-x-0 before:bottom-0 before:z-[1] before:border-b before:border-disabled-border',
          tabBarClassName
        )}
      >
        <div
          className={cn(
            'pointer-events-none absolute bottom-0 left-0 top-0 z-[2] w-12 bg-gradient-to-r from-white to-transparent transition-opacity duration-300',
            canScrollLeft ? 'opacity-100' : 'opacity-0'
          )}
        />

        <div
          className={cn(
            'pointer-events-none absolute bottom-0 right-0 top-0 z-[2] w-12 bg-gradient-to-l from-white to-transparent transition-opacity duration-300',
            canScrollRight ? 'opacity-100' : 'opacity-0'
          )}
        />

        <div
          ref={scrollRef}
          onScroll={checkScrollState}
          className="ds-tabs-nav-scroll overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <div
            ref={tabListRef}
            role="tablist"
            className={cn(
              'relative flex items-center font-iransans text-[14px] leading-[22px]',
              fullWidth ? 'w-full gap-x-0' : 'w-max min-w-full gap-x-8'
            )}
          >
            {items.map((item) => {
              const selected = item.key === currentKey;
              return (
                <button
                  key={item.key}
                  ref={(node) => {
                    tabRefs.current[item.key] = node;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  disabled={item.disabled}
                  id={`tab-${item.key}`}
                  aria-controls={`panel-${item.key}`}
                  className={cn(
                    'ds-tabs-tab relative z-[1] inline-flex items-center border-0 bg-transparent text-neutral-fg transition-colors',
                    'cursor-pointer whitespace-nowrap outline-none',
                    'hover:text-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange',
                    'disabled:cursor-not-allowed disabled:text-disabled-fg',
                    fullWidth
                      ? 'min-w-0 flex-1 shrink justify-center px-0 py-1.5'
                      : 'inline-flex shrink-0 px-0 py-[12px]',
                    selected && (fullWidth ? 'font-bold text-orange' : 'font-normal text-orange')
                  )}
                  onClick={() => {
                    if (!item.disabled) setActiveKey(item.key);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
            {inkBar != null && (
              <div
                aria-hidden
                className={cn(
                  'ds-tabs-ink-bar pointer-events-none absolute bottom-0 z-[2] h-0.5 bg-orange',
                  inkAnimate && 'transition-[left,width] duration-300'
                )}
                style={{left: inkBar.left, width: inkBar.width}}
              />
            )}
          </div>
        </div>
      </div>
      {activeItem?.children != null && (
        <div
          role="tabpanel"
          id={`panel-${activeItem.key}`}
          aria-labelledby={`tab-${activeItem.key}`}
          className="pt-4"
        >
          {activeItem.children}
        </div>
      )}
    </div>
  );
}
